import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';


/* ==========================================================================
   Tvati Header State
   ========================================================================== */

const mobileMenuMediaQuery = window.matchMedia('(max-width: 749px)');

let tvatiChromeRaf = null;
let tvatiChromeScrollTarget = null;
let tvatiAnnouncementResizeObserver = null;


/**
 * Returns the visible bottom edge of the announcement bar.
 *
 * At the top of the page this is the announcement height.
 * Once the announcement has scrolled out of view this becomes 0.
 *
 * @returns {number}
 */
const getTvatiAnnouncementBottom = () => {
  const announcement = document.querySelector('.tvati-announcement-section');

  if (!(announcement instanceof HTMLElement)) return 0;

  const rect = announcement.getBoundingClientRect();

  return Math.max(0, Math.round(rect.bottom));
};


/**
 * Keeps Tvati-specific CSS state in sync with the real page position.
 */
const updateTvatiHeaderChromeState = () => {
  tvatiChromeRaf = null;

  const root = document.documentElement;
  const announcement = document.querySelector('.tvati-announcement-section');
  const announcementHeight =
    announcement instanceof HTMLElement
      ? Math.round(announcement.getBoundingClientRect().height)
      : 0;

  const announcementBottom = getTvatiAnnouncementBottom();
  const scrollTop = getScrollTop();
  const threshold = announcementHeight || 8;

  if (!root.classList.contains('tvati-mobile-menu-open')) {
    root.style.setProperty(
      '--tvati-mobile-menu-top',
      `${announcementBottom}px`
    );
  }

  root.classList.toggle(
    'tvati-page-scrolled',
    scrollTop > Math.max(8, threshold - 2)
  );
};


const queueTvatiHeaderChromeState = () => {
  if (tvatiChromeRaf !== null) return;

  tvatiChromeRaf = requestAnimationFrame(updateTvatiHeaderChromeState);
};


const bindTvatiHeaderChromeState = () => {
  const nextScrollTarget = getScrollEventTarget();

  if (tvatiChromeScrollTarget !== nextScrollTarget) {
    tvatiChromeScrollTarget?.removeEventListener(
      'scroll',
      queueTvatiHeaderChromeState
    );

    tvatiChromeScrollTarget = nextScrollTarget;

    tvatiChromeScrollTarget?.addEventListener(
      'scroll',
      queueTvatiHeaderChromeState,
      { passive: true }
    );
  }

  tvatiAnnouncementResizeObserver?.disconnect();
  tvatiAnnouncementResizeObserver = null;

  const announcement = document.querySelector('.tvati-announcement-section');

  if (announcement instanceof HTMLElement) {
    tvatiAnnouncementResizeObserver = new ResizeObserver(
      queueTvatiHeaderChromeState
    );

    tvatiAnnouncementResizeObserver.observe(announcement);
  }

  queueTvatiHeaderChromeState();
};


/* ==========================================================================
   Theme Header Component
   ========================================================================== */

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up'
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global --header-height custom property up to date.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const roundedHeaderHeight = Math.round(
      entry.borderBoxSize[0].blockSize
    );

    document.body.style.setProperty(
      '--header-height',
      `${roundedHeaderHeight}px`
    );

    if (
      this.#menuDrawerHiddenWidth &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }

    queueTvatiHeaderChromeState();
  });

  /**
   * Observes the header while scrolling the viewport to track sticky state.
   * @param {Boolean} alwaysSticky
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting
          ? 'inactive'
          : 'active';

        if (this.dataset.themeColor) {
          changeMetaThemeColor(this.dataset.themeColor);
        }
      } else {
        this.#offscreen =
          !isIntersecting ||
          this.dataset.stickyState === 'active';
      }

      queueTvatiHeaderChromeState();
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu.
   * @param {CustomEvent<{ minimumReached: boolean }>} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates desktop menu visibility.
   * @param {boolean} hideMenu
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;

      const overflowList = this.querySelector('overflow-list');

      if (overflowList) {
        overflowList.removeAttribute('minimum-reached');
      }
    }

    setHeaderMenuStyle();
  }

  /**
   * Rebinds scroll state when the theme changes scroll container.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');

    if (!stickyMode) return;

    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener(
        'scroll',
        this.#handleWindowScroll
      );

      this.#scrollContainer = getScrollEventTarget();

      this.#scrollContainer.addEventListener(
        'scroll',
        this.#handleWindowScroll
      );
    }

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    this.#observeStickyPosition(stickyMode === 'always');

    bindTvatiHeaderChromeState();
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;

      this.#updateScrollState();
      updateTvatiHeaderChromeState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');

    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);
    this.addEventListener(
      'overflowMinimum',
      this.#handleOverflowMinimum
    );

    const stickyMode = this.getAttribute('sticky');

    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (
        stickyMode === 'scroll-up' ||
        stickyMode === 'always'
      ) {
        this.#scrollContainer = getScrollEventTarget();

        this.#scrollContainer.addEventListener(
          'scroll',
          this.#handleWindowScroll
        );
      }

      scrollContainerMediaQuery.addEventListener(
        'change',
        this.#handleBreakpointChange
      );
    }

    queueTvatiHeaderChromeState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();

    this.removeEventListener(
      'overflowMinimum',
      this.#handleOverflowMinimum
    );

    scrollContainerMediaQuery.removeEventListener(
      'change',
      this.#handleBreakpointChange
    );

    this.#scrollContainer?.removeEventListener(
      'scroll',
      this.#handleWindowScroll
    );

    this.#scrollContainer = null;

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}


/* ==========================================================================
   Tvati Mobile Menu
   ========================================================================== */

/**
 * Freezes the visible floating bar exactly where it is before the menu opens.
 * The panel then uses the bar's measured bottom edge + the shared menu gap,
 * so spacing is identical whether the announcement bar is visible or gone.
 *
 * @param {HTMLElement} header
 */
const freezeTvatiMobileMenuGeometry = (header) => {
  const root = document.documentElement;
  const bar = header.querySelector('.tvati-header__bar');

  if (!(bar instanceof HTMLElement)) return;

  const barRect = bar.getBoundingClientRect();
  const announcementBottom = getTvatiAnnouncementBottom();
  const rootStyles = getComputedStyle(root);
  const configuredGap = parseFloat(
    rootStyles.getPropertyValue('--tvati-mobile-menu-gap')
  );
  const menuGap = Number.isFinite(configuredGap)
    ? configuredGap
    : 38;

  const contentOffset = Math.max(
    0,
    barRect.bottom - announcementBottom + menuGap
  );

  root.style.setProperty(
    '--tvati-mobile-menu-top',
    `${announcementBottom}px`
  );

  root.style.setProperty(
    '--tvati-mobile-header-top',
    `${barRect.top}px`
  );

  root.style.setProperty(
    '--tvati-mobile-header-left',
    `${barRect.left}px`
  );

  root.style.setProperty(
    '--tvati-mobile-header-width',
    `${barRect.width}px`
  );

  root.style.setProperty(
    '--tvati-mobile-menu-content-offset',
    `${contentOffset}px`
  );
};

const clearTvatiMobileMenuGeometry = () => {
  const root = document.documentElement;

  root.style.removeProperty('--tvati-mobile-header-top');
  root.style.removeProperty('--tvati-mobile-header-left');
  root.style.removeProperty('--tvati-mobile-header-width');
  root.style.removeProperty('--tvati-mobile-menu-content-offset');
};

const initialiseTvatiMobileMenu = () => {
  const header = document.getElementById('header-component');
  const toggle = header?.querySelector('[data-tvati-mobile-toggle]');
  const panelId = toggle?.getAttribute('aria-controls');
  const panel = panelId
    ? document.getElementById(panelId)
    : null;
  const toggleLabel = header?.querySelector(
    '[data-tvati-mobile-toggle-label]'
  );

  if (
    !(header instanceof HTMLElement) ||
    !(toggle instanceof HTMLButtonElement) ||
    !(panel instanceof HTMLElement)
  ) {
    return;
  }

  if (toggle.dataset.tvatiMenuReady === 'true') return;

  toggle.dataset.tvatiMenuReady = 'true';

  let closeTimer = null;
  let headerReleaseAnimation = null;


  /*
   * While the mobile menu is open, CSS temporarily freezes the visible
   * floating bar with position:fixed. Releasing that state in one frame can
   * create a tiny snap if its natural sticky position differs by a pixel or
   * two. Capture the frozen rectangle, release the menu geometry, then FLIP
   * the bar smoothly back to its natural position.
   */
  const releaseFrozenMobileHeader = (immediate = false) => {
    const root = document.documentElement;
    const bar = header.querySelector('.tvati-header__bar');

    headerReleaseAnimation?.cancel();
    headerReleaseAnimation = null;

    const before =
      bar instanceof HTMLElement
        ? bar.getBoundingClientRect()
        : null;

    root.classList.remove('tvati-mobile-menu-open');
    clearTvatiMobileMenuGeometry();

    /*
     * Update page-scrolled / announcement state immediately so the target
     * geometry we measure below is the actual post-menu header position.
     */
    updateTvatiHeaderChromeState();

    if (
      immediate ||
      !(bar instanceof HTMLElement) ||
      !before ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    const after = bar.getBoundingClientRect();
    const deltaX = before.left - after.left;
    const deltaY = before.top - after.top;

    if (
      Math.abs(deltaX) < 0.5 &&
      Math.abs(deltaY) < 0.5
    ) {
      return;
    }

    headerReleaseAnimation = bar.animate(
      [
        {
          transform: `translate3d(${deltaX}px, ${deltaY}px, 0)`
        },
        {
          transform: 'translate3d(0, 0, 0)'
        }
      ],
      {
        duration: 180,
        easing: 'cubic-bezier(.22, 1, .36, 1)'
      }
    );

    headerReleaseAnimation.addEventListener(
      'finish',
      () => {
        headerReleaseAnimation = null;
      },
      { once: true }
    );
  };

  const setAccessibility = (open) => {
    toggle.setAttribute(
      'aria-expanded',
      open ? 'true' : 'false'
    );

    toggle.setAttribute(
      'aria-label',
      open ? 'Close menu' : 'Open menu'
    );

    panel.setAttribute(
      'aria-hidden',
      open ? 'false' : 'true'
    );

    panel.inert = !open;

    if (toggleLabel) {
      toggleLabel.textContent =
        open ? 'Close menu' : 'Open menu';
    }
  };

  const finishClose = (immediateRelease = false) => {
    window.clearTimeout(closeTimer);

    panel.classList.remove(
      'is-open',
      'is-closing'
    );

    header.classList.remove(
      'tvati-header--menu-open'
    );

    setAccessibility(false);

    /*
     * Do not visually restyle the header here. CSS keeps the same glass
     * surface throughout the entire open / close cycle.
     */
    releaseFrozenMobileHeader(immediateRelease);

    queueTvatiHeaderChromeState();
  };

  const openMenu = () => {
    if (!mobileMenuMediaQuery.matches) return;

    window.clearTimeout(closeTimer);

    headerReleaseAnimation?.cancel();
    headerReleaseAnimation = null;

    /*
     * Measure the announcement BEFORE the menu locks the page.
     * This gives us the exact visible top offset at the moment
     * the user opens the menu.
     */
    updateTvatiHeaderChromeState();
    freezeTvatiMobileMenuGeometry(header);

    panel.classList.remove('is-closing');
    panel.scrollTop = 0;

    header.classList.add(
      'tvati-header--menu-open'
    );

    document.documentElement.classList.add(
      'tvati-mobile-menu-open'
    );

    setAccessibility(true);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.classList.add('is-open');
      });
    });
  };

  const closeMenu = (immediate = false) => {
    if (
      !panel.classList.contains('is-open') &&
      !panel.classList.contains('is-closing')
    ) {
      finishClose(true);
      return;
    }

    window.clearTimeout(closeTimer);

    panel.classList.remove('is-open');
    panel.classList.add('is-closing');

    /*
     * Start the X -> hamburger animation immediately, but deliberately keep
     * `tvati-mobile-menu-open` on <html> until the panel transition finishes.
     * That keeps the header pinned and the page locked throughout the close,
     * avoiding the old snap/jump.
     */
    header.classList.remove(
      'tvati-header--menu-open'
    );

    setAccessibility(false);

    if (immediate) {
      finishClose(true);
      return;
    }

    closeTimer = window.setTimeout(
      () => finishClose(false),
      310
    );
  };

  toggle.addEventListener('click', () => {
    if (!mobileMenuMediaQuery.matches) return;

    if (panel.classList.contains('is-open')) {
      closeMenu();
    } else {
      openMenu();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (
      event.key === 'Escape' &&
      panel.classList.contains('is-open')
    ) {
      closeMenu();
      toggle.focus();
    }
  });

  panel.addEventListener('click', (event) => {
    if (
      event.target instanceof Element &&
      event.target.closest('a[href]')
    ) {
      closeMenu(true);
    }
  });

  const submenus = [
    ...panel.querySelectorAll(
      '.tvati-header__mobile-submenu'
    ),
  ];

  submenus.forEach((submenu) => {
    submenu.addEventListener('toggle', () => {
      if (!submenu.open) return;

      submenus.forEach((otherSubmenu) => {
        if (otherSubmenu !== submenu) {
          otherSubmenu.open = false;
        }
      });
    });
  });

  mobileMenuMediaQuery.addEventListener(
    'change',
    (event) => {
      if (!event.matches) {
        closeMenu(true);
      }
    }
  );

  setAccessibility(false);
};


/* ==========================================================================
   Page Ready
   ========================================================================== */

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  bindTvatiHeaderChromeState();
  initialiseTvatiMobileMenu();

  window.addEventListener(
    'resize',
    queueTvatiHeaderChromeState,
    { passive: true }
  );

  scrollContainerMediaQuery.addEventListener(
    'change',
    bindTvatiHeaderChromeState
  );

  /*
   * Update header group height on resize of any child.
   */
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce(
        (totalHeight, entry) => {
          if (
            entry.target !== header ||
            (
              header?.hasAttribute('transparent') &&
              header.parentElement?.nextElementSibling
            )
          ) {
            return (
              totalHeight +
              (entry.borderBoxSize[0]?.blockSize ?? 0)
            );
          }

          return totalHeight;
        },
        0
      );

      const roundedHeaderGroupHeight =
        Math.round(headerGroupHeight);

      document.body.style.setProperty(
        '--header-group-height',
        `${roundedHeaderGroupHeight}px`
      );

      queueTvatiHeaderChromeState();
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    const children = headerGroup.children;

    for (let i = 0; i < children.length; i++) {
      const element = children[i];

      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    const mutationObserver = new MutationObserver(
      (mutations) => {
        for (const mutation of mutations) {
          if (mutation.type !== 'childList') continue;

          const currentChildren = headerGroup.children;

          for (
            let i = 0;
            i < currentChildren.length;
            i++
          ) {
            const element = currentChildren[i];

            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }

        queueTvatiHeaderChromeState();
      }
    );

    mutationObserver.observe(
      headerGroup,
      { childList: true }
    );
  }
});


/* ==========================================================================
   Shopify Theme Editor
   ========================================================================== */

document.addEventListener(
  'shopify:section:load',
  () => {
    bindTvatiHeaderChromeState();
    initialiseTvatiMobileMenu();
  }
);
